import { useState, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Send, Hash, Paperclip, Image, FileText, X, UserPlus, Users, Camera, FolderOpen, MoreVertical, Flag, Ban, CheckCircle } from 'lucide-react'
import { apiUrl } from '../config/api'
import usePusherChannel from '../hooks/usePusher'
import Avatar from '../components/ui/Avatar'
import Button from '../components/ui/Button'

/** Comprime imagem antes do upload — reduz 3-8MB pra ~100-200KB */
function compressImage(file: File, maxWidth = 1200, quality = 0.75): Promise<File> {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) { resolve(file); return }

    const img = new window.Image()
    img.onload = () => {
      let { width, height } = img
      if (width <= maxWidth) { resolve(file); return }

      // Redimensiona mantendo proporção
      height = Math.round((height * maxWidth) / width)
      width = maxWidth

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        (blob) => {
          if (!blob) { resolve(file); return }
          const compressed = new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' })
          resolve(compressed)
        },
        'image/jpeg',
        quality,
      )
    }
    img.onerror = () => resolve(file)
    img.src = URL.createObjectURL(file)
  })
}

interface Message {
  id: number; user_id: number; user_name: string; content: string | null
  media_url: string | null; media_name: string | null; media_type: string | null; media_size: string | null
  created_at: string
}

interface ChannelInfo {
  id: number; name: string; slug: string; color: string | null; description: string | null
  is_subscribed?: boolean; members_count?: number
}

export default function ChatPage() {
  const { channel: channelSlug } = useParams()
  const [loading, setLoading] = useState(true)
  const [channelInfo, setChannelInfo] = useState<ChannelInfo | null>(null)
  const [subscribed, setSubscribed] = useState(false)
  const [subscribing, setSubscribing] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [attachMenu, setAttachMenu] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const documentInputRef = useRef<HTMLInputElement>(null)
  const [currentUserId, setCurrentUserId] = useState(0)
  const [msgPage, setMsgPage] = useState(1)
  const [hasOlderMessages, setHasOlderMessages] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const isFirstLoad = useRef(true)
  const [blockedIds, setBlockedIds] = useState<Set<number>>(new Set())
  const [fullscreenImg, setFullscreenImg] = useState<string | null>(null)
  const [actionSheet, setActionSheet] = useState<{ msg: Message } | null>(null)
  const [reportStep, setReportStep] = useState<'menu' | 'reason' | 'done'>('menu')
  const [reportReason, setReportReason] = useState('')
  const [reportDesc, setReportDesc] = useState('')
  const [reportSending, setReportSending] = useState(false)
  const [blockConfirm, setBlockConfirm] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)

  useEffect(() => {
    const cached = localStorage.getItem('terms_accepted')
    if (cached === 'true') setTermsAccepted(true)
    const user = localStorage.getItem('user')
    if (user) try { setCurrentUserId(JSON.parse(user).id) } catch {}
    fetchChannelAndMessages()
    fetchBlocks()
  }, [channelSlug])

  // WebSocket — recebe mensagens em tempo real via Soketi
  usePusherChannel(
    channelInfo ? `community.channel.${channelInfo.id}` : null,
    'new-message',
    (data: Message) => {
      setMessages(prev => {
        const dataId = Number(data.id)
        if (prev.some(m => Number(m.id) === dataId)) return prev
        if (blockedIds.has(data.user_id)) return prev
        return [...prev, data]
      })
    },
  )

  useEffect(() => {
    if (isFirstLoad.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'instant' })
      isFirstLoad.current = false
    }
  }, [messages.length])

  // Scroll pro bottom quando receber mensagem nova via WebSocket
  useEffect(() => {
    if (!isFirstLoad.current && messages.length > 0) {
      const container = scrollContainerRef.current
      if (container) {
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150
        if (isNearBottom) bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [messages.length])

  // Carregar mensagens mais antigas ao chegar no topo
  function handleScroll() {
    const container = scrollContainerRef.current
    if (!container || loadingOlder || !hasOlderMessages || !channelInfo) return
    if (container.scrollTop < 80) {
      fetchMessagesForChannel(channelInfo.id, msgPage + 1, true)
    }
  }

  async function fetchChannelAndMessages() {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}`, Accept: 'application/json' }

      // Buscar canais pra encontrar o canal pelo slug
      const chRes = await fetch(apiUrl('/v1/community/my/channels'), { headers })
      const chData = await chRes.json()
      const ch = (chData.data || []).find((c: any) => c.slug === channelSlug)

      if (ch) {
        setChannelInfo(ch)
        setSubscribed(ch.is_subscribed)
        await fetchMessagesForChannel(ch.id)
      }
    } catch {} finally { setLoading(false) }
  }


  async function fetchMessagesForChannel(channelId: number, page = 1, prepend = false) {
    if (prepend) setLoadingOlder(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(apiUrl(`/v1/community/my/channels/${channelId}/messages?per_page=30&page=${page}`), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      const data = await res.json()
      const msgs: Message[] = data.data || []
      const meta = data.meta || {}

      if (prepend) {
        // Guardar scroll position antes de adicionar
        const container = scrollContainerRef.current
        const prevHeight = container?.scrollHeight || 0

        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id))
          const newOnes = msgs.filter(m => !existingIds.has(m.id))
          return [...newOnes, ...prev]
        })

        // Restaurar scroll position após render
        requestAnimationFrame(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - prevHeight
          }
        })
      } else {
        setMessages(msgs)
        isFirstLoad.current = true
      }

      setHasOlderMessages(page < (meta.last_page || 1))
      setMsgPage(page)
    } catch {} finally { setLoadingOlder(false) }
  }

  async function fetchBlocks() {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(apiUrl('/v1/community/my/blocks'), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      const data = await res.json()
      if (data.data) setBlockedIds(new Set(data.data.map((b: any) => b.user_id)))
    } catch {}
  }

  async function handleReport() {
    if (!actionSheet || !reportReason) return
    setReportSending(true)
    try {
      const token = localStorage.getItem('token')
      await fetch(apiUrl('/v1/community/my/report'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reported_user_id: actionSheet.msg.user_id,
          message_id: actionSheet.msg.id,
          reason: reportReason,
          description: reportDesc || undefined,
        }),
      })
      setReportStep('done')
    } catch {}
    setReportSending(false)
  }

  async function handleBlock() {
    if (!actionSheet) return
    try {
      const token = localStorage.getItem('token')
      await fetch(apiUrl('/v1/community/my/block'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ blocked_user_id: actionSheet.msg.user_id }),
      })
      const blockedUserId = actionSheet.msg.user_id
      setBlockedIds(prev => new Set([...prev, blockedUserId]))
      setMessages(prev => prev.filter(m => m.user_id !== blockedUserId))
      closeActionSheet()
    } catch {}
  }

  function closeActionSheet() {
    setActionSheet(null)
    setReportStep('menu')
    setReportReason('')
    setReportDesc('')
    setBlockConfirm(false)
  }

  async function handleSubscribe() {
    if (!channelInfo) return
    setSubscribing(true)
    try {
      const token = localStorage.getItem('token')
      // Aceitar termos se ainda não aceitou
      if (!localStorage.getItem('terms_accepted')) {
        await fetch(apiUrl('/v1/community/my/accept-terms'), {
          method: 'POST', headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        })
        localStorage.setItem('terms_accepted', 'true')
      }
      await fetch(apiUrl(`/v1/community/my/channels/${channelInfo.id}/subscribe`), {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      setSubscribed(true)
    } catch {} finally { setSubscribing(false) }
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault()
    if ((!input.trim() && !pendingFile) || !channelInfo) return
    setSending(true)
    try {
      const token = localStorage.getItem('token')
      const fd = new FormData()
      if (input.trim()) fd.append('content', input)
      if (pendingFile) {
        const fileToSend = await compressImage(pendingFile)
        fd.append('file', fileToSend)
      }
      await fetch(apiUrl(`/v1/community/my/channels/${channelInfo.id}/messages`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      })
      setInput('')
      setPendingFile(null)
    } catch {} finally { setSending(false) }
  }

  const handleFileSelect = (type: 'image' | 'document') => {
    setAttachMenu(false)
    if (type === 'image') imageInputRef.current?.click()
    else documentInputRef.current?.click()
  }

  const formatTime = (d: string) => new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" />
      </div>
    )
  }

  if (!channelInfo) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-content-muted">Canal não encontrado</p>
      </div>
    )
  }

  // Tela de inscrição
  if (!subscribed) {
    const slug = window.location.pathname.split('/')[1] || ''
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-sm w-full text-center">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center text-2xl font-bold text-white mx-auto mb-6"
            style={{ backgroundColor: channelInfo.color || '#5865F2' }}
          >
            {channelInfo.name.substring(0, 2).toUpperCase()}
          </div>
          <h2 className="text-xl font-bold text-content-normal mb-2">{channelInfo.name}</h2>
          <p className="text-sm text-content-muted mb-4">
            {channelInfo.description || 'Inscreva-se pra participar das discussões.'}
          </p>
          <div className="flex items-center justify-center gap-2 mb-6 text-sm text-content-muted">
            <Users size={16} />{channelInfo.members_count || 0} membros
          </div>

          {/* Diretrizes da comunidade — sempre visíveis no join do canal,
              mesmo que o user já tenha aceito os termos globais. Apple
              guideline 1.2 exige reforço explícito antes do user acessar
              conteúdo gerado por outros usuários. */}
          <div className="w-full bg-surface-secondary rounded-xl p-4 mb-4 text-left text-xs text-content-muted space-y-2">
            <p className="font-semibold text-content-normal">Diretrizes deste canal</p>
            <p>
              Tolerância zero a conteúdo ofensivo, discriminatório ou abusivo.
              Mensagens denunciadas serão revisadas em até 24h e podem ser removidas.
            </p>
            <p>
              Você pode <strong>denunciar mensagens</strong> e <strong>bloquear usuários</strong> a
              qualquer momento tocando nos três pontos da mensagem.
            </p>
            <a
              href={`/${slug}/termos`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline inline-block"
            >
              Ler termos de uso completos
            </a>
          </div>

          <label className="flex items-start gap-2 mb-4 text-left cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={e => setTermsAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-border-subtle accent-primary shrink-0"
            />
            <span className="text-xs text-content-muted">
              Li e concordo com as diretrizes acima e os Termos de Uso da comunidade.
            </span>
          </label>

          <Button onClick={handleSubscribe} loading={subscribing} disabled={!termsAccepted} fullWidth icon={<UserPlus size={18} />}>
            Inscrever-se no canal
          </Button>
        </div>
      </div>
    )
  }

  // Chat
  let lastUserId: number | null = null

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header desktop */}
      <div className="h-12 px-4 items-center gap-2 border-b border-border-subtle shrink-0 hidden lg:flex">
        <Hash size={20} className="text-content-muted" />
        <h1 className="text-base font-semibold text-content-normal flex-1">{channelInfo.name}</h1>
      </div>

      {/* Messages */}
      <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto min-h-0 px-3 md:px-4 py-4 flex flex-col">
        {/* Carregando mais antigas */}
        {loadingOlder && (
          <div className="flex justify-center py-3">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        <div className="flex flex-col gap-1 mt-auto">
          {messages.length === 0 && (
            <p className="text-sm text-content-muted text-center py-8">Nenhuma mensagem ainda. Envie a primeira!</p>
          )}
          {messages.map(msg => {
            const isNewGroup = msg.user_id !== lastUserId
            const isMe = msg.user_id === currentUserId
            lastUserId = msg.user_id

            return (
              <div key={msg.id} className={`group flex gap-2.5 ${isNewGroup ? 'mt-3' : 'mt-0.5'}`}>
                <div className="w-9 shrink-0">
                  {isNewGroup && <Avatar name={msg.user_name} size="sm" />}
                </div>
                <div className="flex-1 min-w-0">
                  {isNewGroup && (
                    <div className="flex items-baseline gap-2 mb-0.5">
                      <span className={`text-sm font-semibold ${isMe ? 'text-primary' : 'text-content-normal'}`}>
                        {msg.user_name}
                      </span>
                      <span className="text-[11px] text-content-muted">{formatTime(msg.created_at)}</span>
                    </div>
                  )}
                  {msg.content && <p className="text-sm text-content-normal leading-relaxed break-words">{msg.content}</p>}
                  {msg.media_url && (
                    <div className="mt-1">
                      {msg.media_type === 'image' ? (
                        <img
                          src={msg.media_url} alt=""
                          className="max-w-xs max-h-64 rounded-lg object-cover cursor-pointer active:opacity-80"
                          onClick={() => setFullscreenImg(msg.media_url)}
                        />
                      ) : (
                        <a href={msg.media_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 bg-surface-secondary border border-border-subtle rounded-lg hover:bg-surface-hover">
                          <FileText size={16} className="text-primary" />
                          <span className="text-sm text-content-normal">{msg.media_name}</span>
                          {msg.media_size && <span className="text-xs text-content-muted">{msg.media_size}</span>}
                        </a>
                      )}
                    </div>
                  )}
                </div>
                {/* Botão report/block — só pra mensagens de outros */}
                {!isMe && (
                  <button
                    onClick={() => { setActionSheet({ msg }); setReportStep('menu'); setBlockConfirm(false) }}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-content-muted hover:bg-surface-active transition-all shrink-0 self-start mt-1 opacity-40 hover:opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                  >
                    <MoreVertical size={16} />
                  </button>
                )}
              </div>
            )
          })}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Arquivo pendente — preview de imagem */}
      {pendingFile && (
        <div className="px-3 md:px-4 shrink-0">
          <div className="flex items-center gap-2 px-3 py-2 bg-surface-secondary rounded-t-xl border border-b-0 border-border-subtle">
            {pendingFile.type.startsWith('image/') ? (
              <img src={URL.createObjectURL(pendingFile)} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
            ) : (
              <FileText size={16} className="text-primary shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <span className="text-sm text-content-normal truncate block">{pendingFile.name}</span>
              <span className="text-[11px] text-content-muted">{(pendingFile.size / 1024).toFixed(0)} KB</span>
            </div>
            <button onClick={() => setPendingFile(null)} className="text-content-muted hover:text-status-danger shrink-0"><X size={14} /></button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-3 md:px-4 pb-3 md:pb-4 shrink-0 relative">
        {attachMenu && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setAttachMenu(false)} />
            <div className="fixed inset-x-0 bottom-0 z-50 lg:hidden">
              <div className="bg-surface-primary border-t border-border-subtle rounded-t-2xl p-4 pb-8">
                <div className="w-10 h-1 bg-border-strong rounded-full mx-auto mb-4" />
                <div className="grid grid-cols-3 gap-4">
                  <button onClick={() => handleFileSelect('image')} className="flex flex-col items-center gap-2 py-3 rounded-xl hover:bg-surface-hover">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"><Camera size={22} className="text-primary" /></div>
                    <span className="text-xs font-medium text-content-normal">Foto</span>
                  </button>
                  <button onClick={() => handleFileSelect('document')} className="flex flex-col items-center gap-2 py-3 rounded-xl hover:bg-surface-hover">
                    <div className="w-12 h-12 rounded-full bg-status-online/10 flex items-center justify-center"><FileText size={22} className="text-status-online" /></div>
                    <span className="text-xs font-medium text-content-normal">Arquivo</span>
                  </button>
                  <button onClick={() => handleFileSelect('image')} className="flex flex-col items-center gap-2 py-3 rounded-xl hover:bg-surface-hover">
                    <div className="w-12 h-12 rounded-full bg-status-idle/10 flex items-center justify-center"><FolderOpen size={22} className="text-status-idle" /></div>
                    <span className="text-xs font-medium text-content-normal">Galeria</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="absolute bottom-full mb-2 left-3 bg-surface-floating border border-border-subtle rounded-xl shadow-xl z-50 overflow-hidden hidden lg:block">
              <button onClick={() => handleFileSelect('image')} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-hover w-full text-left">
                <Image size={16} className="text-primary" /><span className="text-sm text-content-normal">Imagem</span>
              </button>
              <button onClick={() => handleFileSelect('document')} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-hover w-full text-left">
                <FileText size={16} className="text-status-online" /><span className="text-sm text-content-normal">Documento</span>
              </button>
            </div>
          </>
        )}

        <form onSubmit={handleSend} className="flex gap-2">
          <button type="button" onClick={() => setAttachMenu(!attachMenu)}
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors shrink-0 ${attachMenu ? 'bg-primary text-white' : 'bg-surface-active text-content-muted'}`}>
            <Paperclip size={18} />
          </button>
          <div className={`flex-1 bg-surface-active flex items-center px-4 ${pendingFile ? 'rounded-b-xl rounded-t-none' : 'rounded-xl'}`}>
            <input type="text" value={input} onChange={e => setInput(e.target.value)}
              placeholder={`Mensagem em #${channelInfo.name}`}
              className="flex-1 bg-transparent py-2.5 text-sm text-content-normal placeholder:text-content-muted outline-none" />
          </div>
          <button type="submit" disabled={sending && !input.trim() && !pendingFile}
            className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-white hover:bg-primary-hover transition-colors disabled:opacity-30 shrink-0">
            <Send size={18} />
          </button>
        </form>

        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) setPendingFile(f) }} />
        <input ref={documentInputRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) setPendingFile(f) }} />
      </div>

      {/* Fullscreen image viewer */}
      {fullscreenImg && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setFullscreenImg(null)}
        >
          <button
            onClick={() => setFullscreenImg(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 z-10"
          >
            <X size={20} />
          </button>
          <img
            src={fullscreenImg}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      {/* Action Sheet — Report / Block */}
      {actionSheet && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={closeActionSheet} />
          <div className="fixed inset-x-0 bottom-0 z-50 bg-surface-primary rounded-t-2xl border-t border-border-subtle max-h-[80vh] overflow-y-auto safe-area-inset-bottom">
            <div className="w-10 h-1 bg-border-strong rounded-full mx-auto mt-3 mb-2" />

            {reportStep === 'menu' && !blockConfirm && (
              <div className="p-4 pb-8 space-y-1">
                <p className="text-xs text-content-muted mb-3 px-1">Mensagem de {actionSheet.msg.user_name}</p>
                <button
                  onClick={() => setReportStep('reason')}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-surface-hover text-left"
                >
                  <Flag size={18} className="text-status-danger shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-content-normal">Denunciar mensagem</p>
                    <p className="text-xs text-content-muted">Reportar conteúdo ofensivo ou inadequado</p>
                  </div>
                </button>
                <button
                  onClick={() => setBlockConfirm(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-surface-hover text-left"
                >
                  <Ban size={18} className="text-status-danger shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-content-normal">Bloquear {actionSheet.msg.user_name}</p>
                    <p className="text-xs text-content-muted">Não ver mais mensagens desta pessoa</p>
                  </div>
                </button>
              </div>
            )}

            {reportStep === 'reason' && (
              <div className="p-4 pb-8">
                <h3 className="text-base font-semibold text-content-normal mb-4">Por que você está denunciando?</h3>
                <div className="space-y-2 mb-4">
                  {[
                    { value: 'spam', label: 'Spam' },
                    { value: 'harassment', label: 'Assédio' },
                    { value: 'hate_speech', label: 'Discurso de ódio' },
                    { value: 'inappropriate', label: 'Conteúdo impróprio' },
                    { value: 'other', label: 'Outro' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setReportReason(opt.value)}
                      className={`w-full text-left px-4 py-3 rounded-xl border transition-colors text-sm ${
                        reportReason === opt.value
                          ? 'border-primary bg-primary/5 text-primary font-medium'
                          : 'border-border-subtle text-content-normal hover:bg-surface-hover'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <textarea
                  value={reportDesc}
                  onChange={e => setReportDesc(e.target.value)}
                  placeholder="Detalhes adicionais (opcional)"
                  className="w-full px-4 py-3 text-sm border border-border-subtle rounded-xl bg-surface-primary text-content-normal placeholder:text-content-muted outline-none focus:border-primary resize-none mb-4"
                  rows={2}
                />
                <Button
                  onClick={handleReport}
                  loading={reportSending}
                  disabled={!reportReason}
                  fullWidth
                  variant="danger"
                >
                  Enviar denúncia
                </Button>
              </div>
            )}

            {reportStep === 'done' && (
              <div className="p-4 pb-8 text-center">
                <div className="w-14 h-14 rounded-full bg-status-online/10 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle size={28} className="text-status-online" />
                </div>
                <h3 className="text-base font-semibold text-content-normal mb-1">Denúncia enviada</h3>
                <p className="text-sm text-content-muted mb-4">Obrigado por ajudar a manter a comunidade segura. Vamos analisar em até 24 horas.</p>
                <Button onClick={closeActionSheet} fullWidth>Fechar</Button>
              </div>
            )}

            {blockConfirm && (
              <div className="p-4 pb-8 text-center">
                <div className="w-14 h-14 rounded-full bg-status-danger/10 flex items-center justify-center mx-auto mb-3">
                  <Ban size={28} className="text-status-danger" />
                </div>
                <h3 className="text-base font-semibold text-content-normal mb-1">
                  Bloquear {actionSheet.msg.user_name}?
                </h3>
                <p className="text-sm text-content-muted mb-4">
                  Você não verá mais as mensagens desta pessoa. Você pode desbloquear depois no seu perfil.
                </p>
                <div className="flex gap-2">
                  <Button onClick={() => setBlockConfirm(false)} variant="secondary" fullWidth>Cancelar</Button>
                  <Button onClick={handleBlock} variant="danger" fullWidth>Bloquear</Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
